// Standalone data layer. Same exports and shapes as the CRM version (src/front-components/utils/lead-desk-data.ts),
// but talks to this folder's own server (server.mjs) instead of the CRM's GraphQL. The screens cannot tell the difference.
import {
  studentFromWebLead,
  studentInputFromForm,
  type DeskRole,
  type StudentForm,
  type StudentRow,
  type WebLeadRow,
} from 'src/utils/lead-desk';

export const STUDENT_CAP = 100_000;
export const WEB_LEAD_CAP = 1000;

const call = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, { ...init, headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) } });
  const body = (await response.json().catch(() => ({}))) as T & { error?: string };

  if (!response.ok) throw new Error(body.error ?? `The server answered ${response.status}.`);

  return body;
};

const qs = (params: Record<string, string>): string => new URLSearchParams(params).toString();

export const studentFilterFor = (role: DeskRole, person: string): string => `${role}:${person}`;

export type StudentsLoad = { rows: StudentRow[]; total: number; truncated: boolean };
export type WebLeadsLoad = { rows: WebLeadRow[]; total: number; truncated: boolean };

export const fetchStudents = (role: DeskRole, person: string): Promise<StudentsLoad> =>
  call<StudentsLoad>(`/api/students?${qs({ role, person })}`);

export const fetchWebLeads = (): Promise<WebLeadsLoad> => call<WebLeadsLoad>('/api/web-leads');

export const createStudent = async (form: StudentForm): Promise<string> =>
  (await call<{ id: string }>('/api/students', { method: 'POST', body: JSON.stringify(studentInputFromForm(form)) })).id;

export const updateStudent = async (id: string, data: Record<string, unknown>): Promise<void> => {
  await call(`/api/students/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
};

export const setStudentBlocked = (id: string, blocked: boolean): Promise<void> => updateStudent(id, { isBlocked: blocked });

export const deleteStudent = async (id: string): Promise<void> => {
  await call(`/api/students/${id}`, { method: 'DELETE' });
};

export const convertWebLead = async (lead: WebLeadRow, counsellorName: string): Promise<string> => {
  if ((lead.status ?? 'NEW') === 'CONVERTED') throw new Error('This enquiry has already been converted.');

  const student = { ...studentFromWebLead(lead), ...(counsellorName.trim() === '' ? {} : { counsellorName: counsellorName.trim() }), stage: 'NEW_LEAD' };

  return (await call<{ id: string }>(`/api/web-leads/${lead.id}/convert`, { method: 'POST', body: JSON.stringify({ student }) })).id;
};

export type DistributeOutcome = { assigned: number; withoutTelecaller: number; given: Record<string, number> };

/** Admin: hand every lead that has no counsellor to the next counsellor (and that counsellor's next telecaller) in the round robin. */
export const distributeLeads = (): Promise<DistributeOutcome> => call<DistributeOutcome>('/api/students/distribute', { method: 'POST', body: '{}' });

export type CallHistoryItem = {
  id: string;
  name?: string | null;
  sessionDate?: string | null;
  status?: string | null;
  counsellor?: string | null;
  summary?: string | null;
};

export const fetchCallHistory = (studentId: string): Promise<CallHistoryItem[]> => call<CallHistoryItem[]>(`/api/students/${studentId}/calls`);

export const fetchRoster = (): Promise<{ counsellors: string[]; telecallers: string[] }> => call('/api/roster');

export type CallLogInput = { status: string; nextCallAt: string; followUpStatus: string; note: string };

/** Log the outcome of a call. The server writes the author and time, applies the status and next call, and refuses a locked student. */
export const logCall = async (studentId: string, form: CallLogInput): Promise<{ final: boolean }> =>
  call<{ id: string; final: boolean }>(`/api/students/${studentId}/call-log`, { method: 'POST', body: JSON.stringify({ ...form, nextCallAt: form.nextCallAt === '' ? '' : new Date(form.nextCallAt).toISOString() }) });

export type WorkspaceMember = { id: string; name?: { firstName?: string | null; lastName?: string | null } | null; userEmail?: string | null };

export const fetchMembers = (): Promise<WorkspaceMember[]> => call<WorkspaceMember[]>('/api/members');

export type ImportOutcome = { created: number; failed: Array<{ name: string; reason: string }> };

export const importStudents = async (forms: StudentForm[], onProgress?: (done: number) => void): Promise<ImportOutcome> => {
  const outcome: ImportOutcome = { created: 0, failed: [] };

  for (const [index, form] of forms.entries()) {
    try {
      await createStudent(form);
      outcome.created += 1;
    } catch (cause) {
      outcome.failed.push({ name: form.name, reason: cause instanceof Error ? cause.message : 'Could not create.' });
    }

    onProgress?.(index + 1);
  }

  return outcome;
};
